import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Calendar, Mail, MessageSquare, Phone, Reply, Sparkles, SquareCheck, Trash2, Users, Workflow, ArrowRightLeft } from "lucide-react";
import { api } from "../../lib/api";
import { usePageTitle as useSharedPageTitle } from "../../lib/page-title";
import {
  ADMIN_KEY,
  ENROLLMENT_STATUS_LABEL,
  LIFECYCLE_LABEL,
  STAGE_LABEL,
  ACCOUNT_STAGE_LABEL,
  useAdminConfig,
  useSequences,
  useTemplates,
  type Activity,
  type Enrollment,
  type Lifecycle,
  type PlaybookId,
  type Signal,
  type Task,
} from "../../lib/admin";
import { Ago, Badge, Button, Callout, Card, CardEmpty, Checkbox, Empty, ErrorState, Field, IconButton, InlineCode, Input, Meter, Modal, None, SelectField, Textarea, cx, notifyError, toaster, useConfirm } from "../../components/ui";

/** Names the page in the browser tab: "{title} · Growth · Metrickle". */
export function usePageTitle(title: string | null | undefined) {
  useSharedPageTitle(title, "Growth");
}

export const invalidateAdmin = (qc: ReturnType<typeof useQueryClient>) => qc.invalidateQueries({ queryKey: ADMIN_KEY });

// --- Small displays ------------------------------------------------------------------------

/** 0–100 health with a band label, so the meaning never rests on color alone. */
export function Health({ score, compact }: { score: number; compact?: boolean }) {
  const band = score >= 70 ? { label: "Healthy", tone: "good" as const } : score >= 40 ? { label: "Fair", tone: "warn" as const } : { label: "Weak", tone: "bad" as const };
  return (
    <span className="inline-flex items-center gap-2" title={`Health ${score}/100 · ${band.label}`}>
      {/* No label: the score is read from the sr-only text beside it. */}
      <Meter value={score} max={100} tone={band.tone} className="w-12" />
      <span className="sr-only">
        Health {score} out of 100, {band.label.toLowerCase()}
      </span>
      <span className="text-xs font-medium text-ink tabular" aria-hidden>
        {score}
      </span>
      {!compact && (
        <span className="text-[11px] text-ink-3" aria-hidden>
          {band.label}
        </span>
      )}
    </span>
  );
}

const LIFECYCLE_TONE: Record<Lifecycle, "good" | "bad" | "neutral" | "accent"> = {
  active: "good",
  dormant: "bad",
  churned: "bad",
  no_app: "neutral",
  not_installed: "neutral",
};

export const LifecycleBadge = ({ value }: { value: Lifecycle }) => <Badge tone={LIFECYCLE_TONE[value]}>{LIFECYCLE_LABEL[value]}</Badge>;
export const StageBadge = ({ value }: { value: keyof typeof STAGE_LABEL }) => (
  <Badge tone={value === "customer" || value === "qualified" ? "accent" : value === "lost" ? "bad" : "neutral"}>{STAGE_LABEL[value]}</Badge>
);
export const AccountStageBadge = ({ value }: { value: keyof typeof ACCOUNT_STAGE_LABEL }) => (
  <Badge tone={value === "customer" || value === "expansion" ? "accent" : value === "at_risk" || value === "churned" ? "bad" : "neutral"}>
    {ACCOUNT_STAGE_LABEL[value]}
  </Badge>
);

export function SignalChips({ signals, max = 3 }: { signals: Signal[]; max?: number }) {
  if (!signals.length) return <None />;
  return (
    <ul className="flex flex-wrap gap-1" aria-label="Signals">
      {signals.slice(0, max).map((s) => (
        <li key={s.label}>
          <Badge tone={s.tone === "neutral" ? "neutral" : s.tone}>{s.label}</Badge>
        </li>
      ))}
    </ul>
  );
}

export function PlayChips({ plays }: { plays: PlaybookId[] }) {
  const config = useAdminConfig();
  const names = new Map(config.data?.playbooks.map((p) => [p.id, p.name]));
  if (!plays.length) return null;
  return (
    <ul className="flex flex-wrap gap-1" aria-label="Matching plays">
      {plays.map((p) => (
        <li key={p}>
          <Link
            to={`/admin/plays#${p}`}
            className="inline-flex min-h-6 items-center rounded-md bg-accent-wash px-1.5 text-[11px] font-medium text-accent-text hover:underline"
          >
            {names.get(p) ?? p}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** A sequence enrollment's status, colored by outcome. */
export function EnrollmentStatusBadge({ status }: { status: Enrollment["status"] }) {
  return (
    <Badge tone={status === "active" ? "accent" : status === "replied" || status === "completed" ? "good" : status === "failed" ? "bad" : "neutral"}>
      {ENROLLMENT_STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

/** Banner shown wherever emails go out while sending isn't configured. */
export function SendingNotice() {
  const config = useAdminConfig();
  if (!config.data || config.data.sending) return null;
  return (
    <Callout tone="warn" role="note" size="xs">
      <strong className="font-medium text-ink">Log-only mode.</strong> Emails are recorded on the timeline but not delivered. Set <InlineCode>OUTREACH_FROM_EMAIL</InlineCode> on
      an onboarded Email Sending domain to send for real.
    </Callout>
  );
}

// --- Timeline ------------------------------------------------------------------------------

const KIND: Record<Activity["kind"], { icon: typeof Mail; label: string }> = {
  note: { icon: MessageSquare, label: "Note" },
  call: { icon: Phone, label: "Call" },
  meeting: { icon: Users, label: "Meeting" },
  email: { icon: Mail, label: "Email" },
  reply: { icon: Reply, label: "Replied" },
  stage: { icon: ArrowRightLeft, label: "Stage" },
  task: { icon: SquareCheck, label: "Task done" },
  sequence: { icon: Workflow, label: "Sequence" },
};

function activityTitle(a: Activity): ReactNode {
  const m = a.meta ?? {};
  if (a.kind === "stage") {
    if (m.unsubscribed) return "Unsubscribed from outreach";
    return (
      <>
        Stage {String(m.from ?? "").replace("_", " ")} <span aria-hidden>→</span>
        <span className="sr-only">to</span> <strong className="font-medium">{String(m.to ?? "").replace("_", " ")}</strong>
      </>
    );
  }
  if (a.kind === "email") {
    return (
      <>
        {a.subject}
        {m.sequence ? (
          <span className="text-ink-3">
            {" "}
            <span aria-hidden>·</span> {String(m.sequence)} step {String(m.step)}
          </span>
        ) : null}
      </>
    );
  }
  return a.subject ?? KIND[a.kind].label;
}

/** Bodies longer than this are clamped to two lines with a "Show more" toggle. */
const CLAMP_AT = 160;

export function Timeline({ items, showWho }: { items: Activity[]; showWho?: boolean }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  if (!items.length) return <CardEmpty>Nothing yet. Log a note or send an email to start the history.</CardEmpty>;
  const toggle = (id: string) =>
    setOpen((o) => {
      const next = new Set(o);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <ol className="divide-y divide-line" aria-label="Activity, newest first">
      {items.map((a) => {
        const k = KIND[a.kind];
        const status = a.kind === "email" ? String(a.meta?.status ?? "") : null;
        const long = (a.body?.length ?? 0) > CLAMP_AT || (a.body?.split("\n").length ?? 0) > 2;
        const expanded = open.has(a.id);
        const bodyId = `activity-body-${a.id}`;
        return (
          <li key={a.id} className="flex gap-3 px-4 py-2.5">
            <k.icon size={15} className="mt-0.5 shrink-0 text-ink-3" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="min-w-0 text-sm text-ink">
                  <span className="sr-only">{k.label}: </span>
                  {activityTitle(a)}
                  {status === "logged" && <span className="ml-1.5 text-[11px] text-ink-3">(logged, not sent)</span>}
                </p>
                <span className="text-xs text-ink-3">
                  <Ago t={a.created_at} />
                </span>
              </div>
              {showWho && (a.contact_name || a.contact_email || a.organization_name) && (
                <div className="text-xs text-ink-3">
                  {a.contact_id ? (
                    <Link to={`/admin/people/${a.contact_id}`} className="hover:text-ink hover:underline">
                      {a.contact_name ?? a.contact_email}
                    </Link>
                  ) : a.organization_id ? (
                    <Link to={`/admin/accounts/${a.organization_id}`} className="hover:text-ink hover:underline">
                      {a.organization_name}
                    </Link>
                  ) : null}
                </div>
              )}
              {a.body && (
                <p id={bodyId} className={cx("mt-0.5 text-[13px] whitespace-pre-wrap text-ink-2", long && !expanded && "line-clamp-2")}>
                  {a.body}
                </p>
              )}
              {long && (
                <Button variant="link" className="mt-0.5 min-h-6 text-xs" aria-expanded={expanded} aria-controls={bodyId} onClick={() => toggle(a.id)}>
                  {expanded ? "Show less" : "Show more"}
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// --- Log activity --------------------------------------------------------------------------

export function LogActivity({ path }: { path: string }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState("note");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api(path, { method: "POST", json: { kind, body: text } });
      setText("");
      await invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-2 px-4 pb-4">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="What happened? Notes, call summary, context for next time…" aria-label="Activity note" />
      <div className="flex items-center justify-between gap-2">
        <SelectField
          aria-label="Activity type"
          className="w-32"
          items={[
            { value: "note", label: "Note" },
            { value: "call", label: "Call" },
            { value: "meeting", label: "Meeting" },
          ]}
          value={kind}
          onChange={setKind}
        />
        <Button size="sm" variant="primary" disabled={busy || !text.trim()}>
          {busy ? "Logging…" : `Log ${kind}`}
        </Button>
      </div>
    </form>
  );
}

// --- Tasks ---------------------------------------------------------------------------------

export const toDateInput = (t: number | null | undefined) => (t ? new Date(t - new Date(t).getTimezoneOffset() * 60_000).toISOString().slice(0, 10) : "");
/** A date input value as local 9am, which reads naturally as "due that day". */
export const fromDateInput = (v: string) => (v ? new Date(`${v}T09:00`).getTime() : null);

export function TaskList({ tasks, contactId, organizationId, showWho }: { tasks: Task[]; contactId?: string; organizationId?: string; showWho?: boolean }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [adding, setAdding] = useState(false);
  const { confirm, dialog } = useConfirm();
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || adding) return;
    setAdding(true);
    try {
      await api("/admin/tasks", { method: "POST", json: { title, dueAt: fromDateInput(due), contactId, organizationId } });
      setTitle("");
      setDue("");
      await invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setAdding(false);
    }
  };
  const toggle = async (t: Task) => {
    await api(`/admin/tasks/${t.id}`, { method: "PATCH", json: { done: !t.done_at } }).catch(notifyError);
    await invalidateAdmin(qc);
  };
  const remove = async (t: Task, i: number) => {
    const next = tasks[i + 1] ?? tasks[i - 1];
    const ok = await confirm({
      title: `Delete the task “${t.title}”?`,
      description: "This can't be undone.",
      confirmLabel: "Delete",
      focusAfter: () => (next ? document.getElementById(`task-delete-${next.id}`) : null),
    });
    if (!ok) return;
    try {
      await api(`/admin/tasks/${t.id}`, { method: "DELETE" });
      toaster.success({ title: "Task deleted" });
    } catch (err) {
      notifyError(err);
    }
    await invalidateAdmin(qc);
  };
  const today = new Date().setHours(23, 59, 59, 999);
  return (
    <div>
      <ul className="divide-y divide-line">
        {tasks.map((t, i) => (
          <li key={t.id} className="group flex items-start gap-3 px-4 py-2">
            <div className="min-w-0 flex-1">
              <Checkbox checked={!!t.done_at} onCheckedChange={() => toggle(t)} labelClassName={cx("text-sm", t.done_at ? "text-ink-3 line-through" : "text-ink")}>
                {t.title}
                {t.done_at && <span className="sr-only"> (done)</span>}
              </Checkbox>
              <div className="flex flex-wrap gap-x-2 pl-6 text-xs text-ink-3">
                {t.due_at && (
                  <span className={cx(!t.done_at && t.due_at < today && "font-medium text-bad")}>
                    <Calendar size={11} className="mr-1 inline" aria-hidden />
                    {!t.done_at && (t.due_at < new Date().setHours(0, 0, 0, 0) ? "Overdue · " : t.due_at < today ? "Due today · " : "Due ")}
                    {new Date(t.due_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                )}
                {showWho && t.contact_id && (
                  <Link to={`/admin/people/${t.contact_id}`} className="hover:text-ink hover:underline">
                    {t.contact_name ?? t.contact_email}
                  </Link>
                )}
                {showWho && !t.contact_id && t.organization_id && (
                  <Link to={`/admin/accounts/${t.organization_id}`} className="hover:text-ink hover:underline">
                    {t.organization_name}
                  </Link>
                )}
              </div>
            </div>
            <IconButton
              id={`task-delete-${t.id}`}
              label={`Delete ${t.title}`}
              tone="danger"
              onClick={() => remove(t, i)}
              className="opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
            >
              <Trash2 size={13} />
            </IconButton>
          </li>
        ))}
        {!tasks.length && <li className="px-4 py-6 text-center text-sm text-ink-3">No open tasks.</li>}
      </ul>
      {(contactId || organizationId) && (
        <form onSubmit={add} className="flex gap-2 border-t border-line px-4 py-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a task…" aria-label="New task" className="h-8 text-[13px]" />
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date" className="h-8 w-36 text-[13px]" />
          <Button size="sm" disabled={adding || !title.trim()}>
            {adding ? "Adding…" : "Add"}
          </Button>
        </form>
      )}
      {dialog}
    </div>
  );
}

export function FollowUpInput({ value, onChange }: { value: number | null; onChange: (t: number | null) => void }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-ink-3">
      Follow up
      <span className="w-40">
        <Input type="date" value={toDateInput(value)} onChange={(e) => onChange(fromDateInput(e.target.value))} aria-label="Next follow-up" className="text-[13px]" />
      </span>
    </label>
  );
}

// --- Compose -------------------------------------------------------------------------------

const GOALS = [
  "Welcome them personally and ask what they want to measure",
  "Help them add their first app",
  "Help them finish the install; no data has arrived yet",
  "Check in because their app went quiet",
  "Congratulate them on growth and offer a quick call",
  "Ask for a testimonial or referral",
  "First cold intro to a prospect who hasn't signed up",
];

/** 1:1 email with templates, merge-field preview and an optional Claude draft. */
export function ComposeModal({
  open,
  onOpenChange,
  contact,
  defaultTemplate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  contact: { id: string; name: string | null; email: string; unsubscribed?: boolean };
  defaultTemplate?: string;
}) {
  const qc = useQueryClient();
  const config = useAdminConfig();
  const templates = useTemplates();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState<{ subject: string; body: string; unknownFields: string[] } | null>(null);
  const [goal, setGoal] = useState(GOALS[0]!);
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState<"send" | "draft" | null>(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    const t = templates.data?.find((x) => x.name === defaultTemplate);
    setSubject(t?.subject ?? "");
    setBody(t?.body ?? "");
  }, [open, defaultTemplate, templates.data]);

  const applyTemplate = (id: string) => {
    const t = templates.data?.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject);
    setBody(t.body);
    setPreview(null);
  };

  const showPreview = async () => {
    try {
      setPreview(await api(`/admin/people/${contact.id}/preview`, { method: "POST", json: { subject, body } }));
    } catch (err) {
      notifyError(err);
    }
  };

  const draft = async () => {
    setBusy("draft");
    try {
      const d = await api<{ subject: string; body: string }>(`/admin/people/${contact.id}/draft`, {
        method: "POST",
        json: { goal, instructions: instructions || null },
      });
      setSubject(d.subject);
      setBody(d.body);
      setPreview(null);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    setBusy("send");
    try {
      const res = await api<{ status: "sent" | "logged" }>(`/admin/people/${contact.id}/email`, { method: "POST", json: { subject, body } });
      toaster.success({ title: res.status === "sent" ? "Email sent" : "Email logged", description: res.status === "logged" ? "Log-only mode: not delivered." : contact.email });
      onOpenChange(false);
      void invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(null);
    }
  };

  const remaining = config.data ? config.data.dailyLimit - config.data.sentToday : null;

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={`Email ${contact.name ?? contact.email}`} description={contact.email} wide>
      {contact.unsubscribed ? (
        <p className="text-sm text-bad">This person unsubscribed from outreach. You can still reply to them from your own inbox.</p>
      ) : (
        <div className="space-y-3">
          <SendingNotice />
          {config.data?.ai && (
            <div role="group" aria-labelledby="ai-draft-heading" className="space-y-2 rounded-lg border border-line bg-surface-2 p-3">
              <div id="ai-draft-heading" className="flex items-center gap-1.5 text-[13px] font-medium text-ink">
                <Sparkles size={14} className="text-accent-text" aria-hidden /> Draft with Claude, from their account signals
              </div>
              <div className="flex flex-col gap-2 @xl:flex-row">
                <SelectField aria-label="Email goal" className="w-full @xl:flex-1" items={GOALS.map((g) => ({ value: g, label: g }))} value={goal} onChange={setGoal} />
                <Button size="md" onClick={draft} disabled={busy !== null}>
                  {busy === "draft" ? "Drafting…" : "Draft"}
                </Button>
              </div>
              <Input value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Optional: extra context, e.g. met at the meetup, mention the React Native SDK" aria-label="Extra instructions" />
            </div>
          )}
          <div className="flex flex-col gap-2 @xl:flex-row @xl:items-end">
            <div className="flex-1">
              <Field label="Subject">
                <Input value={subject} onChange={(e) => (setSubject(e.target.value), setPreview(null))} />
              </Field>
            </div>
            <SelectField
              aria-label="Start from template"
              placeholder="Start from template…"
              className="w-full @xl:w-56"
              items={(templates.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
              value={undefined}
              onChange={applyTemplate}
            />
          </div>
          <Field label="Body" hint={<>Merge fields like <InlineCode>{"{{first_name | there}}"}</InlineCode> fill in per person. An unsubscribe line is added automatically.</>}>
            <Textarea value={body} onChange={(e) => (setBody(e.target.value), setPreview(null))} rows={10} className="font-mono leading-relaxed" />
          </Field>
          {/* Announces draft and preview results, which otherwise change the form silently. */}
          <p className="sr-only" role="status" aria-live="polite">
            {busy === "draft" ? "Drafting with Claude…" : busy === "send" ? "Sending…" : preview ? "Preview ready below the body." : ""}
          </p>
          {preview && (
            <div className="rounded-lg border border-line bg-surface-2 p-3" aria-label="Preview" role="region">
              <div className="mb-1 text-xs font-medium text-ink-3">Preview as {contact.name ?? contact.email}</div>
              <div className="text-sm font-medium text-ink">{preview.subject}</div>
              <p className="mt-2 text-[13px] whitespace-pre-wrap text-ink-2">{preview.body}</p>
              {!!preview.unknownFields.length && (
                <p role="alert" className="mt-2 text-xs text-bad">
                  Unknown merge fields: {preview.unknownFields.join(", ")}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <span className="text-xs text-ink-3">{remaining !== null && `${remaining} of ${config.data!.dailyLimit} sends left today`}</span>
            <div className="flex gap-2">
              <Button onClick={showPreview} disabled={!subject || !body}>
                Preview
              </Button>
              <Button variant="primary" onClick={send} disabled={!subject.trim() || !body.trim() || busy !== null}>
                <Mail size={14} /> {busy === "send" ? "Sending…" : config.data?.sending ? "Send" : "Log email"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// --- Enroll --------------------------------------------------------------------------------

export function EnrollModal({ open, onOpenChange, contactIds, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; contactIds: string[]; onDone?: () => void }) {
  const qc = useQueryClient();
  const sequences = useSequences();
  const [seq, setSeq] = useState<string>();
  const [busy, setBusy] = useState(false);
  const active = (sequences.data ?? []).filter((s) => s.active);
  const submit = async () => {
    if (!seq) return;
    setBusy(true);
    try {
      const res = await api<{ enrolled: number; skipped: number }>(`/admin/sequences/${seq}/enroll`, { method: "POST", json: { contactIds } });
      toaster.success({
        title: `Enrolled ${res.enrolled} ${res.enrolled === 1 ? "person" : "people"}`,
        description: res.skipped ? `${res.skipped} skipped: already enrolled, unsubscribed, or already engaged.` : "The first step goes out on the next run.",
      });
      onOpenChange(false);
      onDone?.();
      void invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Add ${contactIds.length} ${contactIds.length === 1 ? "person" : "people"} to a sequence`}
      description="Sequences send each step on schedule and stop automatically when someone replies, unsubscribes or moves to engaged."
    >
      {sequences.isError ? (
        <ErrorState title="Couldn't load sequences" error={sequences.error} />
      ) : active.length ? (
        <div className="space-y-3">
          <SendingNotice />
          <SelectField
            aria-label="Sequence"
            className="w-full"
            placeholder="Choose a sequence…"
            items={active.map((s) => ({ value: s.id, label: `${s.name} · ${s.steps.length} step${s.steps.length === 1 ? "" : "s"}` }))}
            value={seq}
            onChange={setSeq}
          />
          <div className="flex justify-end">
            <Button variant="primary" onClick={submit} disabled={!seq || busy}>
              {busy ? "Enrolling…" : "Enroll"}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-ink-2">
          No active sequences yet. <Link to="/admin/outreach" className="text-accent-text underline underline-offset-2">Create one in Outreach</Link>.
        </p>
      )}
    </Modal>
  );
}

/** Renders the console only for platform admins; everyone else gets a plain not-found. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const config = useAdminConfig();
  if (config.isPending) return null;
  if (config.isError)
    return (
      <Card>
        <ErrorState title="Couldn't load this page" error={config.error} />
      </Card>
    );
  if (!config.data) return <Empty title="Page not found" />;
  return <>{children}</>;
}
